import { createContext, useContext, useReducer, ReactNode, useEffect, useCallback } from 'react'
import {
  DreamityUserSearchResDto,
  DreamityUserModifyReqDto,
} from '../types/user.types'
import { SERVICE_NAME } from '@/config/api'
import { userApi } from '@/services/api/user.api'
import { getEmailFromToken, isTokenExpired } from '@/utils/jwt'
import { useToastContext } from './ToastContext'
import { isWebViewReady } from '@/utils/platform'
import { nativeGoogleAuthService } from '@/services/nativeGoogleAuthService'
import { nativeAppleAuthService } from '@/services/nativeAppleAuthService'
import { redirectToAppleSignIn } from '@/services/appleSignInWebService'
import { saveGuestId, getGuestId, removeGuestId } from '@/utils/storage'
import { useLanguage } from '@/hooks/useLanguage'
import { t } from '@/utils/i18n'
import {
  getLoginErrorMessage,
  isAppleUserCancellationError,
} from '@/utils/loginErrorUtils'

/** React StrictMode 이중 마운트 등으로 `/common/user/detail`이 동시에 두 번 나가는 것 방지 */
let nonSilentUserDetailInFlight: Promise<void> | null = null

/**
 * 소셜 로그인(Google/Apple) 진행 중 플래그.
 * 네이티브 인증 화면이 떠 있는 동안 앱이 백그라운드로 갔다가 돌아올 때
 * fetchCurrentUser 가 401을 받아 토큰을 삭제하는 것을 방지합니다.
 */
let socialLoginInProgress = false

interface UserState {
  currentUser: DreamityUserSearchResDto | null
  allUsers: DreamityUserSearchResDto[]
  isAuthenticated: boolean
  loading: boolean
  error: string | null
}

type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_USER'; payload: DreamityUserSearchResDto | null }
  | { type: 'SET_ALL_USERS'; payload: DreamityUserSearchResDto[] }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'LOGOUT' }

interface UserContextType extends UserState {
  // 구글 로그인
  loginWithGoogle: (guestId?: string) => void
  // Apple 로그인
  loginWithApple: (guestId?: string) => void
  // OAuth 콜백 처리
  handleOAuthCallback: (token: string) => Promise<void>
  // 현재 유저 정보 조회 (options.silent: true면 로딩 플래그 없이 갱신, PRO 구매 후 등)
  fetchCurrentUser: (options?: { silent?: boolean }) => Promise<void>
  // 전체 유저 조회
  fetchAllUsers: () => Promise<void>
  // 유저 수정
  updateUser: (userData: DreamityUserModifyReqDto) => Promise<void>
  // 유저 삭제
  deleteUser: () => Promise<void>
  // 로그아웃
  logout: () => Promise<void>
  // 게스트 로그인
  guestLogin: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

const initialState: UserState = {
  currentUser: null,
  allUsers: [],
  isAuthenticated: false,
  loading: true, // 초기 로딩 상태를 true로 설정 (토큰 확인 중)
  error: null,
}

function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload }
    case 'SET_ERROR':
      return { ...state, error: action.payload }
    case 'SET_CURRENT_USER':
      const newState = {
        ...state,
        currentUser: action.payload,
        // currentUser가 null이어도 기존 인증 상태 유지 (네트워크 에러 등으로 인한 경우)
        // currentUser가 설정되면 인증 상태도 true로 설정
        isAuthenticated: action.payload !== null ? true : state.isAuthenticated,
      }
      return newState
    case 'SET_ALL_USERS':
      return { ...state, allUsers: action.payload }
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload }
    case 'LOGOUT':
      return {
        ...state,
        currentUser: null,
        isAuthenticated: false,
        allUsers: [],
      }
    default:
      return state
  }
}

interface UserProviderProps {
  children: ReactNode
}

export function UserProvider({ children }: UserProviderProps) {
  const [state, dispatch] = useReducer(userReducer, initialState)
  const { showToast } = useToastContext()
  const { language } = useLanguage()
  const emitAuthStorageChanged = () => {
    try {
      window.dispatchEvent(new Event('auth-storage-changed'))
    } catch (_) {}
  }

  /**
   * 3. 유저 단건 조회
   * JWT 인증 필요, 현재 로그인한 유저 정보 조회
   * @param options.silent - true면 로딩을 켜지 않고 갱신만 함 (PRO 구매 후 등)
   */
  const fetchCurrentUser = useCallback(async (options?: { silent?: boolean }) => {
    // 이미 진행 중인 요청이 있으면 대기 (silent 여부 상관없이 1개만 유지)
    if (nonSilentUserDetailInFlight) {
      await nonSilentUserDetailInFlight
      return
    }

    const run = async () => {
      if (!options?.silent) {
        dispatch({ type: 'SET_LOADING', payload: true })
      }
      dispatch({ type: 'SET_ERROR', payload: null })
      try {
        const user = await userApi.fetchCurrentUser()
        // Normalize backend response: some backends return `id`, our types expect `userId`
        const normalizedUser = {
          ...user,
          userId: (user as any).userId ?? (user as any).id ?? (user as any).userId,
        }
        dispatch({ type: 'SET_CURRENT_USER', payload: normalizedUser })
      } catch (error: any) {
        console.error('[fetchCurrentUser] API 호출 실패:', error)
        const errorMessage = (error as Error).message
        const isUnauthorized = error?.response?.status === 401 || error?.response?.status === 400
        
        if (isUnauthorized) {
          // 소셜 로그인 진행 중에는 401/400이 와도 토큰/인증 상태를 건드리지 않음
          // (Google/Apple 인증 화면이 열려 있는 동안 앱이 백그라운드 전환 후 복귀할 때 발생)
          if (socialLoginInProgress) {
            console.warn(`[fetchCurrentUser] 소셜 로그인 진행 중 ${error?.response?.status} 무시 (loginInProgress=true)`)
          } else {
            console.error(`[fetchCurrentUser] ${error?.response?.status} 에러 - 인증 실패 처리`)
            dispatch({ type: 'SET_CURRENT_USER', payload: null })
            dispatch({ type: 'SET_AUTHENTICATED', payload: false })
            dispatch({ type: 'SET_ERROR', payload: errorMessage })
            localStorage.removeItem('token')
            emitAuthStorageChanged()
          }
        } else {
          console.warn('[fetchCurrentUser] 일시적 에러(네트워크/타임아웃) - 인증 상태 유지:', errorMessage)
        }
      } finally {
        if (!options?.silent) {
          dispatch({ type: 'SET_LOADING', payload: false })
        }
      }
    }

    // Promise를 전역 변수에 저장하여 중복 호출 방지
    const p = run().finally(() => {
      nonSilentUserDetailInFlight = null
    })
    nonSilentUserDetailInFlight = p
    await p
  }, [])

  // 앱 시작 시 토큰이 있으면 즉시 인증 상태 설정 (백엔드 호출 없음)
  // 유저 상세 정보는 백그라운드에서 조회
  useEffect(() => {
    const currentPath = window.location.pathname
    if (currentPath === '/login' ||
        currentPath.startsWith('/login/') ||
        currentPath === '/auth/callback' ||
        currentPath.startsWith('/auth/callback')) {
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    const token = localStorage.getItem('token')

    if (!token) {
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    // 만료된 토큰이면 로그인 유도
    if (isTokenExpired(token)) {
      console.warn('[UserContext] 저장된 토큰 만료 - 로그인 유도')
      localStorage.removeItem('token')
      emitAuthStorageChanged()
      dispatch({ type: 'SET_AUTHENTICATED', payload: false })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }

    // ✅ 유효한 토큰 → 백엔드 호출 없이 즉시 인증 상태 설정
    dispatch({ type: 'SET_AUTHENTICATED', payload: true })
    dispatch({ type: 'SET_LOADING', payload: false })

    // 백그라운드에서 유저 상세 정보 조회 (실패해도 인증 상태 유지)
    fetchCurrentUser({ silent: true }).catch((err) => {
      console.warn('[UserContext] 백그라운드 유저 조회 실패 (인증 상태 유지):', err)
    })
  }, [fetchCurrentUser])

  /**
   * 2-3. OAuth 콜백 처리 (ID 토큰/Code 교환 후 최종 JWT 처리)
   * URL 파라미터나 쿠키에서 토큰을 받아서 처리
   */
  const handleOAuthCallback = useCallback(async (token: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      // JWT 토큰을 localStorage에 저장 (이미 저장되어 있을 수 있지만 확실히 하기 위해)
      localStorage.setItem('token', token)
      emitAuthStorageChanged()
      dispatch({ type: 'SET_AUTHENTICATED', payload: true })
      
      
      // 로그인 후 현재 유저 정보 조회
      // fetchCurrentUser가 실패해도 토큰이 있으면 인증 상태는 유지
      try {
        await fetchCurrentUser()
        // 게스트 → 소셜 연동 후에는 저장된 guestId 제거 (동일 계정 중복 생성 방지)
        removeGuestId()
        try {
          window.dispatchEvent(new Event('LOGIN_SUCCESS'))
        } catch (e) {
          console.warn('LOGIN_SUCCESS 이벤트 디스패치 실패', e)
        }
      } catch (fetchError: any) {
        console.warn('[handleOAuthCallback] fetchCurrentUser 실패, 하지만 토큰이 있으므로 인증 상태 유지:', fetchError)
        const rawMessage = (fetchError as Error).message || ''
        const isNetworkError = rawMessage.includes('Network Error') ||
                              rawMessage.includes('ERR_NETWORK') ||
                              rawMessage.includes('ERR_FAILED') ||
                              rawMessage === 'Redirect'

        // 네트워크 에러나 리다이렉트 에러인 경우는 인증 상태 유지
        // 401 에러인 경우에만 인증 실패로 처리
        const isUnauthorized = fetchError?.response?.status === 401

        if (isUnauthorized) {
          console.error('[handleOAuthCallback] 401 에러 - 인증 실패')
          localStorage.removeItem('token')
          emitAuthStorageChanged()
          dispatch({ type: 'SET_AUTHENTICATED', payload: false })
          dispatch({ type: 'SET_ERROR', payload: getLoginErrorMessage(fetchError, rawMessage || '인증에 실패했습니다.') })
          dispatch({ type: 'SET_LOADING', payload: false })
          return
        }
        if (!isNetworkError) {
          console.warn('[handleOAuthCallback] 기타 에러 - 인증 상태 유지')
          dispatch({ type: 'SET_ERROR', payload: getLoginErrorMessage(fetchError, rawMessage || '일시적인 오류가 발생했습니다.') })
        } else {
          console.warn('[handleOAuthCallback] 네트워크 에러 - 인증 상태 유지')
        }
      }
      
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      // handleOAuthCallback 자체가 실패한 경우 (토큰 저장 실패 등)
      console.error('[handleOAuthCallback] 전체 실패:', error)
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }, [fetchCurrentUser, language])

  /**
   * 2-1. 구글 로그인
   * 네이티브 플랫폼: 네이티브 SDK 사용 (idToken 방식)
   * 웹 플랫폼: REST API 방식 (팝업 + authorization code)
   * @param guestId 게스트 유저의 경우 guestId 전달 (선택적)
   */
  const loginWithGoogle = useCallback(async (providedGuestId?: string) => {
    const guestId = providedGuestId
    // WebView(앱) 환경인 경우 네이티브 SDK 사용 (팝업은 WebView에서 차단됨)
    if (isWebViewReady()) {
      socialLoginInProgress = true  // 로그인 진행 중 플래그 set
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })
        
        // 네이티브 Google 로그인 실행 (이 Promise가 resolve될 때까지 앱이 백그라운드 전환됨)
        const result = await nativeGoogleAuthService.signIn()
        
        // 백엔드로 idToken 전송하여 JWT 받기
        const response = await userApi.googleLoginWithToken(result.idToken, SERVICE_NAME, guestId)
        
        // 토큰 저장 및 인증 상태 설정
        await handleOAuthCallback(response.token)
        
        dispatch({ type: 'SET_LOADING', payload: false })
        try {
          window.dispatchEvent(new Event('LOGIN_SUCCESS'))
        } catch (e) {
          console.warn('LOGIN_SUCCESS 이벤트 디스패치 실패', e)
        }
      } catch (error: any) {
        console.error('[loginWithGoogle] 네이티브 로그인 실패:', error)
        // 로그인 화면에서의 Google 로그인은 ‘연동’이 아니라 ‘로그인’ 이므로 isGuestLinking=false
        const errorMessage = getLoginErrorMessage(error, t('messages.loginFailed', language), false)
        dispatch({ type: 'SET_ERROR', payload: errorMessage })
        dispatch({ type: 'SET_LOADING', payload: false })
        showToast(errorMessage, 'error')
      } finally {
        socialLoginInProgress = false  // 성공/실패 모두 플래그 해제
      }
      return
    }
    
    // 웹 플랫폼인 경우 기존 팝업 방식 사용
    // 1. 구글 로그인 URL을 API로 받아옴
    // 2. 팝업으로 구글 로그인 열기
    // 3. 콜백에서 authorization code 받기
    // 4. code로 JWT 토큰 받기
    const loginWithGoogleWeb = async (webGuestId: string | undefined = guestId) => {
    // 웹에서 시뮬레이터 없이 테스트: ?redirect_login=1 이면 같은 창 리다이렉트만 사용
    const useRedirectOnly = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('redirect_login') === '1'

    let popup: Window | null = null
    let messageListener: ((event: MessageEvent) => void) | null = null
    let timeoutId: any = null
    
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })

      if (useRedirectOnly) {
        const authUrl = await userApi.getGoogleLoginUrl(SERVICE_NAME, guestId)
        window.location.href = authUrl
        return
      }
      
      // 팝업 창 설정
      const width = 500
      const height = 600
      const left = (window.screen.width - width) / 2
      const top = (window.screen.height - height) / 2
      
      // 팝업 이름을 고유하게 설정하여 중복 방지
      const popupName = `googleLogin_${Date.now()}`
      
      // 중요: 사용자 클릭 이벤트와 직접 연결되도록 먼저 빈 팝업을 엽니다
      // API 호출 전에 팝업을 열어야 브라우저가 팝업을 차단하지 않습니다
      // open popup
      popup = window.open(
        'about:blank',
        popupName,
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      )
      
      // 팝업이 null이면 차단된 것 → 같은 창에서 리다이렉트 (웹에서 시뮬레이터 없이 테스트 가능)
      if (!popup) {
        try {
          const authUrl = await userApi.getGoogleLoginUrl(SERVICE_NAME, webGuestId)
          showToast(t('messages.loginInThisWindow', language), 'info')
          window.location.href = authUrl
        } catch (apiError) {
          console.error('[loginWithGoogle] 구글 로그인 URL 가져오기 실패:', apiError)
          dispatch({ type: 'SET_LOADING', payload: false })
          dispatch({ type: 'SET_ERROR', payload: t('messages.googleLoginUrlFetchFailed', language) })
          showToast(t('messages.googleLoginUrlFetchFailed', language), 'error')
        }
        return
      }

      // 1. 구글 로그인 URL 가져오기 (팝업이 이미 열린 후)
      let authUrl: string
      try {
        authUrl = await userApi.getGoogleLoginUrl(SERVICE_NAME, webGuestId)
        // redirect_uri는 백엔드 URL(http://localhost:8080/login/oauth2/code/google)이어야 함
        // 백엔드가 이 경로를 받아서 처리한 후 프론트엔드로 리다이렉트해야 함
      } catch (apiError) {
        console.error('[loginWithGoogle] 구글 로그인 URL 가져오기 실패:', apiError)
        // 팝업 닫기
        try {
          popup.close()
        } catch (e) {
          // 무시
        }
        const errorMsg = t('messages.googleLoginUrlFetchFailed', language)
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_ERROR', payload: errorMsg })
        showToast(errorMsg, 'error')
        return
      }
      
      // 2. 팝업에 구글 로그인 URL 설정
      try {
        popup.location.href = authUrl
        
      } catch (e) {
        // COOP 정책으로 인해 popup.location.href 설정이 실패할 수 있음
        console.warn('[loginWithGoogle] popup.location.href 설정 실패 (COOP 정책), 팝업을 닫고 새로 열기 시도:', e)
        try {
          popup.close()
        } catch (closeError) {
          // 무시
        }
        // 팝업을 닫고 새로 열기
        popup = window.open(
          authUrl,
          popupName,
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
        )
        if (!popup) {
        const errorMsg = t('messages.popupBlocked', language)
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_ERROR', payload: errorMsg })
        showToast(errorMsg, 'error')
        return
        }
      }
      
      const cleanup = () => {
        if (messageListener) {
          window.removeEventListener('message', messageListener)
          messageListener = null
        }
        if (timeoutId) {
          clearTimeout(timeoutId)
          timeoutId = null
        }
      }
      
      // 팝업에서 메시지 받기
      let isProcessing = false // 중복 처리 방지 플래그
      messageListener = (event: MessageEvent) => {
        // 디버그 로그는 필요시에만 출력
        if (event.data.type !== 'GOOGLE_LOGIN_SUCCESS' && event.data.type !== 'GOOGLE_LOGIN_ERROR') {
          return
        }
        
        // 보안을 위해 origin 확인
        if (event.origin !== window.location.origin) {
          console.warn('[loginWithGoogle] 알 수 없는 origin에서 메시지 수신:', {
            received: event.origin,
            expected: window.location.origin
          })
          return
        }
        
        if (event.data.type === 'GOOGLE_LOGIN_SUCCESS') {
          // 이미 처리 중이면 무시 (중복 호출 방지)
          if (isProcessing) {
            return
          }
          
          
          const code = event.data.code
          
          if (code) {
            isProcessing = true // 처리 시작 플래그 설정
            
            // 타임아웃 취소
            if (timeoutId) {
              clearTimeout(timeoutId)
              timeoutId = null
            }
            
            // 3. authorization code로 JWT 토큰 받기
            userApi.googleLogin(code, SERVICE_NAME, webGuestId)
              .then((result) => {
                
                
                // 4. 토큰 저장 및 인증 상태 설정
                return handleOAuthCallback(result.token)
              })
              .then(() => {
                
                isProcessing = false // 처리 완료 플래그 해제
                cleanup() // 메시지 리스너 제거 (중복 호출 방지)
                // 팝업은 OAuthCallback에서 스스로 닫음 (opener에서 close 시 COOP 경고 발생)
              dispatch({ type: 'SET_LOADING', payload: false })
              try {
                window.dispatchEvent(new Event('LOGIN_SUCCESS'))
              } catch (e) {
                console.warn('LOGIN_SUCCESS 이벤트 디스패치 실패', e)
              }
              })
              .catch((error) => {
                const err = error as { response?: { data?: { message?: string } }; message?: string }
                console.error('[loginWithGoogle] 로그인 처리 실패:', err?.response?.data ?? err)
                isProcessing = false // 처리 실패 시에도 플래그 해제
                const errorMessage = getLoginErrorMessage(err, t('messages.loginFailed', language), !!webGuestId)
                dispatch({ type: 'SET_ERROR', payload: errorMessage })
                dispatch({ type: 'SET_LOADING', payload: false })
                cleanup() // 메시지 리스너 제거
                // 팝업은 OAuthCallback에서 스스로 닫음 (opener에서 close 시 COOP 경고 발생)
                showToast(errorMessage, 'error')
              })
          }
        } else if (event.data.type === 'GOOGLE_LOGIN_ERROR') {
          
          console.error('[loginWithGoogle] 구글 로그인 에러:', event.data.error)
          const raw = event.data.error || ''
          const errorMessage = getLoginErrorMessage(
            { message: raw, response: { status: 500, data: { message: raw } } },
            t('messages.loginFailed', language),
            !!webGuestId
          )
          
          // 타임아웃 취소
          if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
          }
          
          dispatch({ type: 'SET_LOADING', payload: false })
          dispatch({ type: 'SET_ERROR', payload: errorMessage })
          cleanup()
          // 팝업은 OAuthCallback에서 스스로 닫음 (opener에서 close 시 COOP 경고 발생)
          showToast(errorMessage, 'error')
        }
      }
      
      // 메시지 리스너 등록
      window.addEventListener('message', messageListener)
      
      
      // 팝업이 닫혔는지 확인 (COOP 정책으로 인해 접근이 차단될 수 있으므로 체크하지 않음)
      // 대신 메시지 리스너와 타임아웃으로 처리
      // checkClosedInterval은 사용하지 않음 (COOP 정책으로 인해 popup.closed 접근 불가)
      
      // 타임아웃 설정 (60초)
      timeoutId = setTimeout(() => {
        console.warn('[loginWithGoogle] 구글 로그인 타임아웃')
        cleanup()
        dispatch({ type: 'SET_LOADING', payload: false })
        dispatch({ type: 'SET_ERROR', payload: t('messages.googleLoginInProgressError', language) })
        showToast(t('messages.googleLoginInProgressError', language), 'error')
      }, 60000) // 60초 타임아웃
      
    } catch (error) {
      console.error('[loginWithGoogle] 구글 로그인 시작 실패:', error)
      const errorMessage = (error as Error).message || t('messages.cannotStartGoogleLogin', language)
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      dispatch({ type: 'SET_LOADING', payload: false })
      showToast(errorMessage, 'error')
    }
    }
    
    // 웹 로그인 실행
    await loginWithGoogleWeb(guestId)
  }, [handleOAuthCallback, language, showToast])

  /**
   * 2-2. 게스트 로그인
   * 저장된 guestId가 있으면 사용하고, 없으면 새로 생성
   * JWT 토큰에서 email을 추출하여 guestId로 저장
   */
  const guestLogin = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      // 저장된 guestId 불러오기
      const savedGuestId = getGuestId()
      
      // 게스트 로그인 API 호출
      const response = await userApi.guestLogin(savedGuestId || undefined)
      
      // JWT 토큰을 localStorage에 저장
      localStorage.setItem('token', response.token)
      emitAuthStorageChanged()
      
      // 다음 게스트 로그인에 넘길 식별자: 서버 guestId > 기존 로컬 값 > JWT(이메일/sub). 이메일만 우선하면 서버가 기대하는 guestId와 달라져 매번 신규 계정이 될 수 있음
      const fromResponse = response.guestId?.trim()
      const fromSaved = savedGuestId?.trim()
      const fromJwt = getEmailFromToken(response.token)?.trim()
      const toPersist = fromResponse || fromSaved || fromJwt
      if (toPersist) {
        saveGuestId(toPersist)
      }
      
      dispatch({ type: 'SET_AUTHENTICATED', payload: true })
      
      // 로그인 후 현재 유저 정보 조회
      await fetchCurrentUser()
      try {
        window.dispatchEvent(new Event('LOGIN_SUCCESS'))
      } catch (e) {
        console.warn('LOGIN_SUCCESS 이벤트 디스패치 실패', e)
      }
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }, [fetchCurrentUser])



  /**
   * 4. 전체 유저 조회
   * JWT 인증 필요, 전체 유저 목록 조회
   */
  const fetchAllUsers = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      const users = await userApi.fetchAllUsers()
      dispatch({ type: 'SET_ALL_USERS', payload: users })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      return
    }
  }, [])

  /**
   * 5. 유저 수정
   * JWT 인증 필요, 현재 로그인한 유저 정보 수정
   */
  const updateUser = useCallback(async (userData: DreamityUserModifyReqDto) => {
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await userApi.updateUser(userData)
      // 수정 후 현재 유저 정보만 갱신 (로딩 플래그 없이 → 초기 화면 재노출 방지)
      await fetchCurrentUser({ silent: true })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      return
    }
  }, [fetchCurrentUser])

  /**
   * 6. 유저 삭제
   * JWT 인증 필요, 현재 로그인한 유저 삭제
   */
  const deleteUser = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await userApi.deleteUser()
      // 삭제 성공 시 로그아웃 처리
      localStorage.removeItem('token')
      emitAuthStorageChanged()
      dispatch({ type: 'SET_CURRENT_USER', payload: null })
      dispatch({ type: 'SET_AUTHENTICATED', payload: false })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
      throw error
    }
  }, [])

  /**
   * 7. 유저 로그아웃
   * JWT 인증 필요, 로그인 세션 쿠키 삭제
   */
  const logout = useCallback(async () => {
    dispatch({ type: 'SET_LOADING', payload: true })
    dispatch({ type: 'SET_ERROR', payload: null })
    try {
      await userApi.logout()
      // localStorage에서 토큰 제거
      localStorage.removeItem('token')
      emitAuthStorageChanged()
      dispatch({ type: 'LOGOUT' })
      dispatch({ type: 'SET_LOADING', payload: false })
    } catch (error) {
      // 에러가 발생해도 로컬 상태는 정리
      localStorage.removeItem('token')
      emitAuthStorageChanged()
      dispatch({ type: 'LOGOUT' })
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message })
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }, [])

  /**
   * 2-4. Apple 로그인
   * 네이티브: SDK → identityToken + name → POST /api/auth/apple/token
   * 웹: Apple 인증 URL로 리다이렉트 → 콜백에서 id_token으로 동일 API 호출
   */
  const loginWithApple = useCallback(async (providedGuestId?: string) => {
    const guestId = providedGuestId
    if (isWebViewReady()) {
      socialLoginInProgress = true  // 로그인 진행 중 플래그 set
      try {
        dispatch({ type: 'SET_LOADING', payload: true })
        dispatch({ type: 'SET_ERROR', payload: null })

        const result = await nativeAppleAuthService.signIn()
        const response = await userApi.appleLoginWithToken(
          result.identityToken,
          SERVICE_NAME,
          result.name,
          guestId
        )
        await handleOAuthCallback(response.token)
        dispatch({ type: 'SET_LOADING', payload: false })
        try {
          window.dispatchEvent(new Event('LOGIN_SUCCESS'))
        } catch (e) {
          console.warn('LOGIN_SUCCESS 이벤트 디스패치 실패', e)
        }
      } catch (error: any) {
        const errorMessage = error?.message || ''
        const isNotSupported =
          errorMessage.includes('APPLE_AUTH_NOT_SUPPORTED') ||
          errorMessage.toLowerCase().includes('not supported on the device') ||
          errorMessage.includes('ios 13 or later')
        if (isNotSupported) {
          // 앱에서 네이티브 Apple Auth 미지원(시뮬레이터/일부 환경) → 웹 폴백으로 진행
          socialLoginInProgress = false
          dispatch({ type: 'SET_LOADING', payload: false })
          dispatch({ type: 'SET_ERROR', payload: null })
          showToast(t('messages.switchToWebLogin', language), 'info')
          redirectToAppleSignIn(guestId)
          return
        }
        console.error('[loginWithApple] 네이티브 로그인 실패:', error)
        const displayMessage = isAppleUserCancellationError(error)
          ? t('messages.loginCancelled', language)
          // 로그인 화면에서의 Apple 로그인은 ‘연동’이 아니라 ‘로그인’ 이므로 isGuestLinking=false
          : getLoginErrorMessage(error, t('messages.loginFailed', language), false)
        dispatch({ type: 'SET_ERROR', payload: displayMessage })
        dispatch({ type: 'SET_LOADING', payload: false })
        showToast(displayMessage, 'error')
      } finally {
        socialLoginInProgress = false  // 성공/실패 모두 플래그 해제
      }
      return
    }

    // 웹: form_post + 백엔드 콜백 (Wimb과 동일). 백엔드가 Dreamity로 리다이렉트(#id_token=...).
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      dispatch({ type: 'SET_ERROR', payload: null })
      redirectToAppleSignIn(guestId)
    } catch (error: any) {
      console.error('[loginWithApple] Apple 로그인 시작 실패:', error)
      const errorMessage = error?.message || t('messages.cannotStartAppleLogin', language)
      dispatch({ type: 'SET_LOADING', payload: false })
      dispatch({ type: 'SET_ERROR', payload: errorMessage })
      showToast(errorMessage, 'error')
    }
  }, [handleOAuthCallback, language, showToast])

  return (
    <UserContext.Provider
      value={{
        ...state,
        loginWithGoogle,
        loginWithApple,
        handleOAuthCallback,
        fetchCurrentUser,
        fetchAllUsers,
        updateUser,
        deleteUser,
        logout,
        guestLogin,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

// useUser hook은 hooks/useUser.ts에서 export합니다
export function useUserContext() {
  const context = useContext(UserContext)
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

